import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { api } from '../lib/api';
import type { AttachmentView, MessageView } from '../lib/api';
import { domainOf } from '../lib/format';
import styles from './MessageBody.module.css';

/**
 * The iframe sandbox for untrusted email.
 *
 * UNCHANGED by the dark-mode work. The dark transform is a *document* rewrite
 * performed in the parent, in an inert DOMParser document, before the HTML ever
 * reaches the frame — it needs no capability inside the frame, so this string is
 * byte-for-byte what it was: 'allow-popups allow-popups-to-escape-sandbox
 * allow-same-origin'.
 *
 * `allow-same-origin` without `allow-scripts` is safe: the document can be
 * measured from the parent, but nothing inside it can run. Adding
 * `allow-scripts` alongside `allow-same-origin` would let untrusted email
 * script the parent document — read the session, exfiltrate every message —
 * because the sandbox would then share this origin AND be able to execute.
 * Never add `allow-scripts` to this string.
 */
export const MESSAGE_SANDBOX = 'allow-popups allow-popups-to-escape-sandbox allow-same-origin';

/**
 * Belt to the sandbox's braces.
 *
 * `img-src` is `'self'` rather than the two API paths it actually needs
 * (`/api/imgproxy` for opted-in remote images, `/api/attachments/` for inline
 * `cid:` parts). CSP source expressions are scheme/host-based and cannot
 * express a path prefix: a bare `/api/imgproxy` is not a valid source, browsers
 * discard it, and the directive silently degrades to `data:` only — which
 * blocks every proxied image *and* every embedded signature logo while looking
 * perfectly correct in the source. Restricting to those paths is the renderer's
 * job anyway, since it is the only thing that ever writes a src.
 *
 * `'self'` is still a real boundary: remote hosts remain unreachable, so the
 * proxy is the only way a sender's server is ever contacted.
 */
export const MESSAGE_CSP = [
  "default-src 'none'",
  "img-src 'self' data:",
  "style-src 'unsafe-inline'",
  'font-src data:',
  "script-src 'none'",
  "frame-src 'none'",
  "object-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
].join('; ');

const TRANSPARENT_PX =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// =============================================================================
// Colour engine
//
// Every value below is a plain sRGB triple with straight alpha. The engine is
// pure and synchronous so `prepareMessageHtml` stays testable, and it never
// touches images — only declared colours.
// =============================================================================

export interface Rgb {
  r: number;
  g: number;
  b: number;
  a: number;
}

const NAMED_COLORS: Readonly<Record<string, string>> = {
  aliceblue: '#f0f8ff', antiquewhite: '#faebd7', aqua: '#00ffff', aquamarine: '#7fffd4',
  azure: '#f0ffff', beige: '#f5f5dc', bisque: '#ffe4c4', black: '#000000',
  blanchedalmond: '#ffebcd', blue: '#0000ff', blueviolet: '#8a2be2', brown: '#a52a2a',
  burlywood: '#deb887', cadetblue: '#5f9ea0', chartreuse: '#7fff00', chocolate: '#d2691e',
  coral: '#ff7f50', cornflowerblue: '#6495ed', cornsilk: '#fff8dc', crimson: '#dc143c',
  cyan: '#00ffff', darkblue: '#00008b', darkcyan: '#008b8b', darkgoldenrod: '#b8860b',
  darkgray: '#a9a9a9', darkgrey: '#a9a9a9', darkgreen: '#006400', darkkhaki: '#bdb76b',
  darkmagenta: '#8b008b', darkolivegreen: '#556b2f', darkorange: '#ff8c00', darkorchid: '#9932cc',
  darkred: '#8b0000', darksalmon: '#e9967a', darkseagreen: '#8fbc8f', darkslateblue: '#483d8b',
  darkslategray: '#2f4f4f', darkslategrey: '#2f4f4f', darkturquoise: '#00ced1', darkviolet: '#9400d3',
  deeppink: '#ff1493', deepskyblue: '#00bfff', dimgray: '#696969', dimgrey: '#696969',
  dodgerblue: '#1e90ff', firebrick: '#b22222', floralwhite: '#fffaf0', forestgreen: '#228b22',
  fuchsia: '#ff00ff', gainsboro: '#dcdcdc', ghostwhite: '#f8f8ff', gold: '#ffd700',
  goldenrod: '#daa520', gray: '#808080', grey: '#808080', green: '#008000',
  greenyellow: '#adff2f', honeydew: '#f0fff0', hotpink: '#ff69b4', indianred: '#cd5c5c',
  indigo: '#4b0082', ivory: '#fffff0', khaki: '#f0e68c', lavender: '#e6e6fa',
  lavenderblush: '#fff0f5', lawngreen: '#7cfc00', lemonchiffon: '#fffacd', lightblue: '#add8e6',
  lightcoral: '#f08080', lightcyan: '#e0ffff', lightgoldenrodyellow: '#fafad2', lightgray: '#d3d3d3',
  lightgrey: '#d3d3d3', lightgreen: '#90ee90', lightpink: '#ffb6c1', lightsalmon: '#ffa07a',
  lightseagreen: '#20b2aa', lightskyblue: '#87cefa', lightslategray: '#778899', lightslategrey: '#778899',
  lightsteelblue: '#b0c4de', lightyellow: '#ffffe0', lime: '#00ff00', limegreen: '#32cd32',
  linen: '#faf0e6', magenta: '#ff00ff', maroon: '#800000', mediumaquamarine: '#66cdaa',
  mediumblue: '#0000cd', mediumorchid: '#ba55d3', mediumpurple: '#9370db', mediumseagreen: '#3cb371',
  mediumslateblue: '#7b68ee', mediumspringgreen: '#00fa9a', mediumturquoise: '#48d1cc', mediumvioletred: '#c71585',
  midnightblue: '#191970', mintcream: '#f5fffa', mistyrose: '#ffe4e1', moccasin: '#ffe4b5',
  navajowhite: '#ffdead', navy: '#000080', oldlace: '#fdf5e6', olive: '#808000',
  olivedrab: '#6b8e23', orange: '#ffa500', orangered: '#ff4500', orchid: '#da70d6',
  palegoldenrod: '#eee8aa', palegreen: '#98fb98', paleturquoise: '#afeeee', palevioletred: '#db7093',
  papayawhip: '#ffefd5', peachpuff: '#ffdab9', peru: '#cd853f', pink: '#ffc0cb',
  plum: '#dda0dd', powderblue: '#b0e0e6', purple: '#800080', rebeccapurple: '#663399',
  red: '#ff0000', rosybrown: '#bc8f8f', royalblue: '#4169e1', saddlebrown: '#8b4513',
  salmon: '#fa8072', sandybrown: '#f4a460', seagreen: '#2e8b57', seashell: '#fff5ee',
  sienna: '#a0522d', silver: '#c0c0c0', skyblue: '#87ceeb', slateblue: '#6a5acd',
  slategray: '#708090', slategrey: '#708090', snow: '#fffafa', springgreen: '#00ff7f',
  steelblue: '#4682b4', tan: '#d2b48c', teal: '#008080', thistle: '#d8bfd8',
  tomato: '#ff6347', turquoise: '#40e0d0', violet: '#ee82ee', wheat: '#f5deb3',
  white: '#ffffff', whitesmoke: '#f5f5f5', yellow: '#ffff00', yellowgreen: '#9acd32',
};

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function byte(value: number): number {
  return Math.round(clamp(value, 0, 255));
}

function hexToRgb(hex: string): Rgb | null {
  const raw = hex.slice(1);
  const expand = (c: string) => parseInt(c + c, 16);
  if (raw.length === 3 || raw.length === 4) {
    const [r, g, b, a] = [raw[0], raw[1], raw[2], raw[3]];
    if (!r || !g || !b) return null;
    if (!/^[0-9a-f]+$/i.test(raw)) return null;
    return { r: expand(r), g: expand(g), b: expand(b), a: a ? expand(a) / 255 : 1 };
  }
  if (raw.length === 6 || raw.length === 8) {
    if (!/^[0-9a-f]+$/i.test(raw)) return null;
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
      a: raw.length === 8 ? parseInt(raw.slice(6, 8), 16) / 255 : 1,
    };
  }
  return null;
}

/** `1`, `50%`, `.5` → 0…1 against the given full-scale reference. */
function component(token: string, scale: number): number | null {
  const t = token.trim();
  if (!t) return null;
  if (t.endsWith('%')) {
    const n = Number(t.slice(0, -1));
    return Number.isFinite(n) ? n / 100 : null;
  }
  const n = Number(t);
  return Number.isFinite(n) ? n / scale : null;
}

function hueDegrees(token: string): number | null {
  const t = token.trim().toLowerCase();
  const m = /^(-?[\d.]+)(deg|grad|rad|turn)?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  switch (m[2]) {
    case 'rad':
      return (n * 180) / Math.PI;
    case 'grad':
      return n * 0.9;
    case 'turn':
      return n * 360;
    default:
      return n;
  }
}

/** Splits a functional-notation body into positional args plus an alpha slot. */
function functionArgs(body: string): { args: string[]; alpha: string | null } {
  const slashed = body.split('/');
  const head = slashed[0] ?? '';
  const alpha = slashed.length > 1 ? (slashed[1] ?? '').trim() : null;
  const args = head
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (alpha === null && args.length === 4) {
    const last = args.pop();
    return { args, alpha: last ?? null };
  }
  return { args, alpha };
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hue < 60) [rp, gp, bp] = [c, x, 0];
  else if (hue < 120) [rp, gp, bp] = [x, c, 0];
  else if (hue < 180) [rp, gp, bp] = [0, c, x];
  else if (hue < 240) [rp, gp, bp] = [0, x, c];
  else if (hue < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return { r: byte((rp + m) * 255), g: byte((gp + m) * 255), b: byte((bp + m) * 255) };
}

function rgbToHsl(c: Rgb): { h: number; s: number; l: number } {
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = 60 * (((g - b) / d) % 6);
  else if (max === g) h = 60 * ((b - r) / d + 2);
  else h = 60 * ((r - g) / d + 4);
  return { h: ((h % 360) + 360) % 360, s: clamp(s, 0, 1), l };
}

function gammaEncode(u: number): number {
  return u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055;
}

/**
 * OKLCh/OKLab exist here for one reason: the accent ramp is written onto
 * <html> as `oklch()` at runtime, so the app's own link colour is unreadable
 * without this conversion.
 */
function oklabToRgb(L: number, a: number, bb: number): { r: number; g: number; b: number } {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * bb;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * bb;
  const s_ = L - 0.0894841775 * a - 1.291485548 * bb;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return {
    r: byte(gammaEncode(clamp(r, 0, 1)) * 255),
    g: byte(gammaEncode(clamp(g, 0, 1)) * 255),
    b: byte(gammaEncode(clamp(b, 0, 1)) * 255),
  };
}

/**
 * Parses every colour syntax that reaches this code: hex, the legacy and modern
 * rgb/hsl forms, oklab/oklch, `transparent`, and the CSS named colours. Anything
 * it cannot read returns null, and every caller treats null as "leave alone".
 */
export function parseColor(input: string): Rgb | null {
  const v = input.trim().toLowerCase();
  if (!v) return null;
  if (v === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  const named = Object.prototype.hasOwnProperty.call(NAMED_COLORS, v) ? NAMED_COLORS[v] : undefined;
  if (named) return hexToRgb(named);
  if (v.startsWith('#')) return hexToRgb(v);

  const fn = /^([a-z]+)\((.*)\)$/s.exec(v);
  if (!fn) return null;
  const name = fn[1] ?? '';
  const { args, alpha } = functionArgs(fn[2] ?? '');
  const a = alpha === null ? 1 : clamp(component(alpha, 1) ?? 1, 0, 1);
  const [a0, a1, a2] = [args[0], args[1], args[2]];
  if (!a0 || !a1 || !a2) return null;

  if (name === 'rgb' || name === 'rgba') {
    const r = component(a0, 1);
    const g = component(a1, 1);
    const b = component(a2, 1);
    if (r === null || g === null || b === null) return null;
    // Percentages already normalised to 0…1; bare numbers are 0…255.
    const toByte = (token: string, value: number) => byte(token.endsWith('%') ? value * 255 : value);
    return { r: toByte(a0, r), g: toByte(a1, g), b: toByte(a2, b), a };
  }

  if (name === 'hsl' || name === 'hsla') {
    const h = hueDegrees(a0);
    const s = component(a1, 100);
    const l = component(a2, 100);
    if (h === null || s === null || l === null) return null;
    return { ...hslToRgb(h, clamp(s, 0, 1), clamp(l, 0, 1)), a };
  }

  if (name === 'oklab' || name === 'oklch') {
    const L = component(a0, 1);
    if (L === null) return null;
    const c1 = Number(a1.endsWith('%') ? Number(a1.slice(0, -1)) / 250 : a1);
    if (!Number.isFinite(c1)) return null;
    if (name === 'oklab') {
      const c2 = Number(a2.endsWith('%') ? Number(a2.slice(0, -1)) / 250 : a2);
      if (!Number.isFinite(c2)) return null;
      return { ...oklabToRgb(clamp(L, 0, 1), c1, c2), a };
    }
    const h = hueDegrees(a2);
    if (h === null) return null;
    const rad = (h * Math.PI) / 180;
    return { ...oklabToRgb(clamp(L, 0, 1), c1 * Math.cos(rad), c1 * Math.sin(rad)), a };
  }

  return null;
}

export function rgbToCss(c: Rgb): string {
  const hex = (n: number) => byte(n).toString(16).padStart(2, '0');
  if (c.a >= 1) return `#${hex(c.r)}${hex(c.g)}${hex(c.b)}`;
  return `rgba(${byte(c.r)}, ${byte(c.g)}, ${byte(c.b)}, ${Number(c.a.toFixed(3))})`;
}

function sameRgb(a: Rgb, b: Rgb): boolean {
  return byte(a.r) === byte(b.r) && byte(a.g) === byte(b.g) && byte(a.b) === byte(b.b) &&
    Math.abs(a.a - b.a) < 0.004;
}

/** Straight-alpha source over an opaque backdrop. */
function composite(src: Rgb, dst: Rgb): Rgb {
  if (src.a >= 1) return { ...src, a: 1 };
  if (src.a <= 0) return { ...dst };
  const a = src.a;
  return {
    r: src.r * a + dst.r * (1 - a),
    g: src.g * a + dst.g * (1 - a),
    b: src.b * a + dst.b * (1 - a),
    a: 1,
  };
}

function channelLuminance(v: number): number {
  const u = v / 255;
  return u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4);
}

function luminance(c: Rgb): number {
  return (
    0.2126 * channelLuminance(c.r) + 0.7152 * channelLuminance(c.g) + 0.0722 * channelLuminance(c.b)
  );
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Colourfulness as a 0…1 fraction of the byte range, NOT HSL saturation.
 *
 * HSL saturation calls `#e8f4ff` (a pale blue tint used as a card background)
 * fully saturated, which would exempt it from the neutral treatment and leave a
 * white-ish card floating in the dark UI. Max-minus-min correctly reads it as
 * near-neutral while still reading `#1a73e8` as a brand colour.
 */
function chromaOf(c: Rgb): number {
  return (Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b)) / 255;
}

function lightnessOf(c: Rgb): number {
  return (Math.max(c.r, c.g, c.b) + Math.min(c.r, c.g, c.b)) / 2 / 255;
}

/** Rewrites lightness (and optionally caps saturation) keeping the hue intact. */
function reshade(c: Rgb, lightness: number, maxSaturation: number): Rgb {
  const { h, s } = rgbToHsl(c);
  return { ...hslToRgb(h, Math.min(s, maxSaturation), clamp(lightness, 0, 1)), a: c.a };
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * clamp(t, 0, 1);
}

// --- Transform thresholds ----------------------------------------------------
//
// These four numbers are the whole policy. They are deliberately conservative:
// anything the engine is not confident about is left exactly as the sender
// authored it, and the contrast floor catches whatever that lets through.

/** Above this colourfulness a value is a brand colour and is never restyled. */
const BRAND_CHROMA = 0.22;
/** At or above this lightness a neutral background is "paper" and becomes the app surface. */
const PAPER_LIGHTNESS = 0.86;
/** Below this lightness a neutral background is already dark and is left alone. */
const DARK_SURFACE_LIGHTNESS = 0.4;
/** At or below this lightness a neutral foreground is "ink" and gets lifted. */
const INK_LIGHTNESS = 0.46;
/** Never ship text below this WCAG ratio against its own resolved backdrop. */
const CONTRAST_FLOOR = 4.0;

export interface Palette {
  /** Only ever constructed for a dark app theme; light theme yields null. */
  surface: Rgb;
  surfaceLightness: number;
  text: Rgb;
  textLightness: number;
  textMuted: Rgb;
  link: Rgb;
  border: Rgb;
  /** The near-black used when a light brand background needs dark text back. */
  ink: Rgb;
  key: string;
}

/**
 * Near-white becomes the app surface; mid neutrals fold into a compressed dark
 * band; anything already dark, and anything with real chroma, is untouched.
 *
 * The mid mapping is continuous at both ends (it meets the surface at
 * PAPER_LIGHTNESS and meets identity at DARK_SURFACE_LIGHTNESS), so two greys
 * that were adjacent in the original stay adjacent here — no banding where a
 * newsletter steps #ffffff → #f7f7f7 → #eeeeee.
 */
function mapBackground(c: Rgb, p: Palette): Rgb | null {
  if (c.a <= 0) return null;
  if (chromaOf(c) > BRAND_CHROMA) return null;
  const l = lightnessOf(c);
  if (l >= PAPER_LIGHTNESS) {
    const next = { ...p.surface, a: c.a };
    return sameRgb(next, c) ? null : next;
  }
  if (l < DARK_SURFACE_LIGHTNESS) return null;
  const t = (PAPER_LIGHTNESS - l) / (PAPER_LIGHTNESS - DARK_SURFACE_LIGHTNESS);
  const target = lerp(p.surfaceLightness, DARK_SURFACE_LIGHTNESS, t);
  const next = reshade(c, target, 0.09);
  return sameRgb(next, c) ? null : next;
}

/**
 * The lightest of the app's own text tones that clears the floor on this
 * backdrop. It always finds one: the worst possible backdrop is the mid-tone
 * where pure white and pure black both sit at 4.58:1, comfortably above the
 * floor, so this function cannot fail to produce readable text.
 */
function bestTextOn(bg: Rgb, p: Palette): Rgb {
  const candidates: Rgb[] = [p.text, p.ink, { r: 255, g: 255, b: 255, a: 1 }, { r: 0, g: 0, b: 0, a: 1 }];
  let best = candidates[0] as Rgb;
  let bestRatio = 0;
  for (const candidate of candidates) {
    const ratio = contrastRatio(candidate, bg);
    if (ratio >= CONTRAST_FLOOR) return candidate;
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }
  return best;
}

/**
 * Walks a failing colour's lightness away from its backdrop until it clears the
 * floor, keeping the hue and nudging saturation up as it goes — Superhuman's
 * own published rule for accents in dark mode, and the reason a brand blue link
 * stays a blue link here instead of being flattened to grey.
 */
function fitContrast(c: Rgb, bg: Rgb, p: Palette): Rgb {
  if (chromaOf(c) <= BRAND_CHROMA) return bestTextOn(bg, p);
  const { h, s } = rgbToHsl(c);
  const saturation = Math.min(1, s * 1.08);
  const away = luminance(bg) > 0.18 ? -1 : 1;
  const from = lightnessOf(c);
  for (let step = 1; step <= 20; step += 1) {
    const l = from + away * step * 0.045;
    if (l <= 0.02 || l >= 0.98) break;
    const candidate: Rgb = { ...hslToRgb(h, saturation, l), a: 1 };
    if (contrastRatio(candidate, bg) >= CONTRAST_FLOOR) return candidate;
  }
  return bestTextOn(bg, p);
}

/**
 * Dark ink is lifted toward the app's text colour on an eased ramp, so the
 * #333/#444 that carries the body copy of most newsletters lands near the app's
 * own primary text while #666 meta copy stays a step quieter. Light text and
 * brand-coloured text pass straight through.
 *
 * `backdropChanged` is what stops this from being Superhuman's bug in the other
 * direction. The floor is only enforced over a pair THIS transform touched: if
 * the sender's own foreground still sits on the sender's own background — white
 * on an orange CTA, say — the combination renders here exactly as it does in
 * every other client, and "fixing" it to black would be us inventing a problem.
 */
function mapForeground(c: Rgb, bg: Rgb, p: Palette, backdropChanged: boolean): Rgb | null {
  if (c.a <= 0) return null;
  let out = c;
  const l = lightnessOf(c);
  if (chromaOf(c) <= BRAND_CHROMA && l <= INK_LIGHTNESS) {
    const t = (l / INK_LIGHTNESS) ** 2;
    out = reshade(c, lerp(p.textLightness, INK_LIGHTNESS, t), 0.12);
  }
  const lifted = !sameRgb(out, c);
  if (!lifted && !backdropChanged) return null;
  if (contrastRatio(composite(out, bg), bg) >= CONTRAST_FLOOR) return lifted ? out : null;
  const floored = fitContrast(out, bg, p);
  return sameRgb(floored, c) ? null : floored;
}

/**
 * Pure white rules keep behaving as spacers (they become the surface they sit
 * on, exactly as they were invisible on white paper); real hairlines land on the
 * app's own border tone; anything already dark or coloured is left alone.
 */
function mapBorder(c: Rgb, p: Palette): Rgb | null {
  if (c.a <= 0) return null;
  if (chromaOf(c) > BRAND_CHROMA) return null;
  const l = lightnessOf(c);
  if (l >= 0.97) {
    const next = { ...p.surface, a: c.a };
    return sameRgb(next, c) ? null : next;
  }
  if (l < 0.5) return null;
  const next = reshade(c, p.surfaceLightness + 0.13, 0.09);
  return sameRgb(next, c) ? null : next;
}

// --- CSS value rewriting -----------------------------------------------------

/** Private-use codepoint: it cannot occur in CSS a sender actually wrote. */
const URL_SENTINEL = '\uE000';

/**
 * Hex, functional notations, then bare words. Order matters: at the position of
 * `rgb(` the functional alternative has to win, or the bare-word branch would
 * swallow the function name and leave a dangling paren.
 */
const COLOR_TOKEN_RE =
  /#[0-9a-f]{3,8}\b|\b(?:rgba?|hsla?|oklab|oklch)\([^()]*\)|\b[a-z]{3,20}\b/gi;

/**
 * Applies `fn` to every colour-looking token in a declaration value, leaving all
 * other text — including whitespace, `!important`, and multi-layer commas —
 * byte-identical. `url(...)` segments are masked first so a fragment like
 * `url(https://x/#ff0000)` is never mistaken for a colour.
 */
function mapColorsInValue(value: string, fn: (c: Rgb) => Rgb | null): string {
  const urls: string[] = [];
  const masked = value.replace(/url\([^()]*\)/gi, (match) => {
    urls.push(match);
    return `${URL_SENTINEL}${urls.length - 1}${URL_SENTINEL}`;
  });
  const mapped = masked.replace(COLOR_TOKEN_RE, (token) => {
    const parsed = parseColor(token);
    if (!parsed) return token;
    const next = fn(parsed);
    return next ? rgbToCss(next) : token;
  });
  return mapped.replace(new RegExp(`${URL_SENTINEL}(\\d+)${URL_SENTINEL}`, 'g'), (_m, i: string) =>
    urls[Number(i)] ?? '',
  );
}

/** The last parseable, non-transparent colour in a value — the one that wins. */
function findColorInValue(value: string): Rgb | null {
  const masked = value.replace(/url\([^()]*\)/gi, ' ');
  let found: Rgb | null = null;
  for (const token of masked.match(COLOR_TOKEN_RE) ?? []) {
    const parsed = parseColor(token);
    if (parsed) found = parsed;
  }
  return found;
}

interface Declaration {
  prop: string;
  value: string;
}

/** Splits on `sep` at paren depth zero and outside quotes. */
function splitTop(input: string, sep: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote = '';
  let current = '';
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i] as string;
    if (quote) {
      current += ch;
      if (ch === quote && input[i - 1] !== '\\') quote = '';
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      current += ch;
      continue;
    }
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === sep && depth === 0) {
      out.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  out.push(current);
  return out;
}

function parseDeclarations(style: string): Declaration[] {
  const out: Declaration[] = [];
  for (const chunk of splitTop(style, ';')) {
    if (!chunk.trim()) continue;
    const parts = splitTop(chunk, ':');
    const prop = (parts[0] ?? '').trim().toLowerCase();
    if (!prop || parts.length < 2) continue;
    out.push({ prop, value: parts.slice(1).join(':') });
  }
  return out;
}

function serializeDeclarations(decls: Declaration[]): string {
  return decls.map((d) => `${d.prop}:${d.value.trim()}`).join('; ');
}

const BG_PROPS = new Set(['background', 'background-color']);
const FG_PROPS = new Set(['color', '-webkit-text-fill-color']);
const BORDER_PROPS = new Set([
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline',
  'outline-color',
]);

// --- Document walk -----------------------------------------------------------

interface ColorContext {
  /** Always opaque: it is the composite of everything above this element. */
  bg: Rgb;
  fg: Rgb;
  /** Did THIS transform put `bg` there, rather than the sender? */
  bgChanged: boolean;
  /** Did THIS transform put `fg` there, rather than the sender? */
  fgChanged: boolean;
}

/** A pathological message must not lock the main thread; it just renders raw. */
const MAX_TRANSFORM_NODES = 15000;

/**
 * Rewrites one element's declared colours and returns the context its children
 * inherit. Backgrounds resolve first because a foreground can only be judged
 * against the backdrop it will actually land on.
 */
function transformElementColors(el: Element, ctx: ColorContext, p: Palette): ColorContext {
  const styleAttr = el.getAttribute('style');
  const decls = styleAttr ? parseDeclarations(styleAttr) : [];
  const bgAttr = el.getAttribute('bgcolor');
  const tag = el.tagName.toLowerCase();
  const colorAttr = tag === 'font' || tag === 'basefont' || tag === 'hr' ? el.getAttribute('color') : null;
  let dirty = false;

  // 1. Background.
  let declaredBg: Rgb | null = null;
  for (const d of decls) {
    if (!BG_PROPS.has(d.prop)) continue;
    const found = findColorInValue(d.value);
    if (found) declaredBg = found;
  }
  if (!declaredBg && bgAttr) declaredBg = parseColor(bgAttr);

  const mappedBg = declaredBg ? mapBackground(declaredBg, p) : null;
  const resolvedBg = mappedBg ?? declaredBg;
  const effBg = resolvedBg ? composite(resolvedBg, ctx.bg) : ctx.bg;
  // A translucent background still shows a backdrop we may have rewritten, so
  // provenance leaks through it rather than being reset by it.
  const bgChanged = declaredBg
    ? mappedBg !== null || (declaredBg.a < 1 && ctx.bgChanged)
    : ctx.bgChanged;

  if (declaredBg) {
    for (const d of decls) {
      if (!BG_PROPS.has(d.prop)) continue;
      const next = mapColorsInValue(d.value, (c) => mapBackground(c, p));
      if (next !== d.value) {
        d.value = next;
        dirty = true;
      }
    }
    if (bgAttr) {
      const parsed = parseColor(bgAttr);
      const next = parsed ? mapBackground(parsed, p) : null;
      if (next) el.setAttribute('bgcolor', rgbToCss(next));
    }
  }

  // 2. Foreground, judged against the background resolved just above.
  let effFg = ctx.fg;
  let declaredFg: Rgb | null = null;
  for (const d of decls) {
    if (!FG_PROPS.has(d.prop)) continue;
    const found = findColorInValue(d.value);
    if (found) declaredFg = found;
  }
  if (!declaredFg && colorAttr) declaredFg = parseColor(colorAttr);

  let fgChanged = ctx.fgChanged;

  if (declaredFg) {
    const mappedFg = mapForeground(declaredFg, effBg, p, bgChanged);
    effFg = composite(mappedFg ?? declaredFg, effBg);
    fgChanged = mappedFg !== null;
    for (const d of decls) {
      if (!FG_PROPS.has(d.prop)) continue;
      const next = mapColorsInValue(d.value, (c) => mapForeground(c, effBg, p, bgChanged));
      if (next !== d.value) {
        d.value = next;
        dirty = true;
      }
    }
    if (colorAttr) {
      const parsed = parseColor(colorAttr);
      const next = parsed ? mapForeground(parsed, effBg, p, bgChanged) : null;
      if (next) el.setAttribute('color', rgbToCss(next));
    }
  } else if (
    declaredBg &&
    (bgChanged || ctx.fgChanged) &&
    contrastRatio(ctx.fg, effBg) < CONTRAST_FLOOR
  ) {
    // Text this element did not colour is about to land on paper one of us
    // changed — the exact shape of the dark-on-dark bug. Pin a colour here.
    const fixed = fitContrast(ctx.fg, effBg, p);
    decls.push({ prop: 'color', value: ` ${rgbToCss(fixed)}` });
    effFg = fixed;
    fgChanged = true;
    dirty = true;
  }

  // 3. Borders and rules.
  for (const d of decls) {
    if (!BORDER_PROPS.has(d.prop)) continue;
    const next = mapColorsInValue(d.value, (c) => mapBorder(c, p));
    if (next !== d.value) {
      d.value = next;
      dirty = true;
    }
  }

  if (dirty) el.setAttribute('style', serializeDeclarations(decls));
  return { bg: effBg, fg: effFg, bgChanged, fgChanged };
}

/**
 * Author stylesheets get the same treatment, minus the per-element backdrop:
 * a rule's `color` is judged against the app surface, which is what the vast
 * majority of it will actually land on.
 *
 * The media-query rewrite is the one thing done for the sender's benefit: an
 * email that ships its own `prefers-color-scheme: dark` block is answering the
 * *OS* preference, which can disagree with the Kiln theme. Forcing the
 * condition to match the app theme means a sender who did the work gets their
 * own dark design instead of ours.
 */
function transformCssText(css: string, p: Palette): string {
  const forced = css
    .replace(/prefers-color-scheme\s*:\s*dark/gi, 'min-width: 0px')
    .replace(/prefers-color-scheme\s*:\s*light/gi, 'min-width: 999999px');

  // Every `url()` comes out of the text before declarations are matched. Inline
  // data: URIs contain semicolons ("data:image/png;base64,…"), and a declaration
  // regex that stops at the first `;` would cut a background shorthand in half —
  // which is how a rewrite corrupts a stylesheet instead of recolouring it.
  const urls: string[] = [];
  const masked = forced.replace(/url\(\s*(?:"[^"]*"|'[^']*'|[^()]*)\)/gi, (match) => {
    urls.push(match);
    return `${URL_SENTINEL}u${urls.length - 1}${URL_SENTINEL}`;
  });

  const mapped = masked.replace(
    /([\w-]+)\s*:\s*([^;{}]+)/g,
    (match: string, rawProp: string, rawValue: string) => {
      const prop = rawProp.toLowerCase();
      const isBg = BG_PROPS.has(prop);
      const isFg = FG_PROPS.has(prop);
      const isBorder = BORDER_PROPS.has(prop);
      if (!isBg && !isFg && !isBorder) return match;
      // Belt to the masking's braces: an unbalanced value is a value this code
      // does not understand, and the safe answer is always "leave it".
      const opens = (rawValue.match(/\(/g) ?? []).length;
      const closes = (rawValue.match(/\)/g) ?? []).length;
      if (opens !== closes) return match;
      const next = mapColorsInValue(rawValue, (c) =>
        isBg
          ? mapBackground(c, p)
          : isFg
            ? // A stylesheet rule cannot be traced to a backdrop, so it is judged
              // against the app surface — which this transform did put there.
              mapForeground(c, p.surface, p, true)
            : mapBorder(c, p),
      );
      return next === rawValue ? match : `${rawProp}: ${next.trim()}`;
    },
  );

  return mapped.replace(
    new RegExp(`${URL_SENTINEL}u(\\d+)${URL_SENTINEL}`, 'g'),
    (_m, i: string) => urls[Number(i)] ?? '',
  );
}

/**
 * The dark transform proper. Runs over the inert parse tree in the parent, so
 * the frame needs no new capability and the sandbox string above is untouched.
 */
function applyDarkTransform(root: Element, p: Palette): void {
  for (const styleEl of Array.from(root.querySelectorAll('style'))) {
    styleEl.textContent = transformCssText(styleEl.textContent ?? '', p);
  }

  let budget = MAX_TRANSFORM_NODES;
  const walk = (el: Element, ctx: ColorContext): void => {
    if (budget <= 0) return;
    budget -= 1;
    const next = transformElementColors(el, ctx, p);
    for (const child of Array.from(el.children)) {
      if (budget <= 0) return;
      const tag = child.tagName.toLowerCase();
      if (tag === 'style' || tag === 'script' || tag === 'img' || tag === 'picture') continue;
      walk(child, next);
    }
  };

  // Both flags start true: replacing the email's implicit white paper with the
  // app surface, and its implicit black ink with the app's text colour, is the
  // first thing this transform does.
  const rootCtx: ColorContext = { bg: p.surface, fg: p.text, bgChanged: true, fgChanged: true };
  for (const child of Array.from(root.children)) {
    const tag = child.tagName.toLowerCase();
    if (tag === 'style' || tag === 'script') continue;
    walk(child, rootCtx);
  }
}

// =============================================================================
// Frame document
// =============================================================================

const LIGHT_FRAME_STYLE = `
  :root { color-scheme: light; }
  html { -webkit-text-size-adjust: 100%; background: transparent; }
  body { margin: 0; padding: 0; overflow-wrap: break-word; word-break: break-word;
         font: 15px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #16161a; background: transparent; }
  img { max-width: 100% !important; height: auto; }
  table { max-width: 100%; }
  a { color: #2b4acb; }
  blockquote { margin: 0 0 0 12px; padding-left: 12px; border-left: 2px solid #d7d7de; color: #4c4c57; }
  pre { white-space: pre-wrap; overflow-wrap: anywhere; }
`;

/**
 * Step (a) and (b) of the transform, and the reason most mail needs nothing
 * else: `color-scheme: dark` lets any email that respects it adapt on its own,
 * and an email that declares no colours simply inherits the app's surface and
 * text here and reads as native.
 */
function darkFrameStyle(p: Palette): string {
  const surface = rgbToCss(p.surface);
  const text = rgbToCss(p.text);
  const muted = rgbToCss(p.textMuted);
  const link = rgbToCss(p.link);
  const border = rgbToCss(p.border);
  return `
  :root { color-scheme: dark; }
  html { -webkit-text-size-adjust: 100%; background: transparent; }
  body { margin: 0; padding: 0; overflow-wrap: break-word; word-break: break-word;
         font: 15px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: ${text}; background: transparent; }
  img { max-width: 100% !important; height: auto; }
  table { max-width: 100%; }
  a { color: ${link}; }
  blockquote { margin: 0 0 0 12px; padding-left: 12px; border-left: 2px solid ${border}; color: ${muted}; }
  hr { border: 0; border-top: 1px solid ${border}; }
  pre, code, kbd, samp { background: ${surface}; }
  pre { white-space: pre-wrap; overflow-wrap: anywhere; }
  ::selection { background: ${border}; color: ${text}; }
`;
}

export function buildSrcDoc(inner: string, palette: Palette | null = null): string {
  const style = palette ? darkFrameStyle(palette) : LIGHT_FRAME_STYLE;
  return (
    `<!doctype html><html><head>` +
    `<meta http-equiv="Content-Security-Policy" content="${MESSAGE_CSP}">` +
    `<base target="_blank">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    `<style>${style}</style></head><body>${inner}</body></html>`
  );
}

// =============================================================================
// Theme observation
//
// The frame is a separate document, so no custom property crosses into it. The
// app's live token values are read off <html> and inlined into the srcdoc. The
// tokens themselves decide light vs dark — nothing here duplicates the theme
// logic that lives in the store.
// =============================================================================

const FALLBACK_DARK: Readonly<Record<string, string>> = {
  '--bg-canvas': '#08080a',
  '--text-primary': '#ededf0',
  '--text-secondary': '#a9a9b2',
  '--text-link': '#b1a9ff',
};

function readToken(cs: CSSStyleDeclaration, name: string): Rgb | null {
  const parsed = parseColor(cs.getPropertyValue(name));
  if (parsed) return parsed;
  const fallback = Object.prototype.hasOwnProperty.call(FALLBACK_DARK, name)
    ? FALLBACK_DARK[name]
    : undefined;
  return fallback ? parseColor(fallback) : null;
}

function readPalette(): Palette | null {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return null;
  const cs = getComputedStyle(document.documentElement);
  const surface = readToken(cs, '--bg-canvas');
  if (!surface) return null;
  const surfaceLightness = lightnessOf(surface);
  // Light theme keeps the paper treatment: there is nothing to transform.
  if (surfaceLightness > 0.5) return null;

  const text = readToken(cs, '--text-primary') ?? { r: 237, g: 237, b: 240, a: 1 };
  const textMuted = readToken(cs, '--text-secondary') ?? { r: 169, g: 169, b: 178, a: 1 };
  const link = readToken(cs, '--text-link') ?? { r: 177, g: 169, b: 255, a: 1 };
  const border = reshade(surface, surfaceLightness + 0.14, 0.12);
  const ink = reshade(surface, 0.05, 0.06);

  return {
    surface,
    surfaceLightness,
    text,
    textLightness: lightnessOf(text),
    textMuted,
    link,
    border,
    ink,
    key: [surface, text, textMuted, link].map(rgbToCss).join('|'),
  };
}

let paletteSnapshot: Palette | null = null;
let paletteRead = false;
const paletteListeners = new Set<() => void>();
let paletteTeardown: (() => void) | null = null;

function refreshPalette(): void {
  const next = readPalette();
  if (next?.key === paletteSnapshot?.key) return;
  paletteSnapshot = next;
  for (const listener of paletteListeners) listener();
}

function getPaletteSnapshot(): Palette | null {
  if (!paletteRead) {
    paletteRead = true;
    paletteSnapshot = readPalette();
  }
  return paletteSnapshot;
}

/**
 * One observer for the whole app rather than one per rendered message: a long
 * thread mounts a dozen of these, and the theme changes for all of them at once.
 */
function subscribePalette(listener: () => void): () => void {
  paletteListeners.add(listener);
  if (!paletteTeardown) {
    // `style` is watched because the accent ramp — and with it --text-link — is
    // written onto <html> as an inline custom property, not via a stylesheet.
    const observer = new MutationObserver(refreshPalette);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'style', 'class'],
    });
    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;
    const onMedia = () => refreshPalette();
    media?.addEventListener('change', onMedia);
    paletteTeardown = () => {
      observer.disconnect();
      media?.removeEventListener('change', onMedia);
    };
  }
  return () => {
    paletteListeners.delete(listener);
    if (paletteListeners.size === 0) {
      paletteTeardown?.();
      paletteTeardown = null;
    }
  };
}

/** The live dark palette, or null whenever the app is in its light theme. */
export function useMessageTheme(): Palette | null {
  return useSyncExternalStore(subscribePalette, getPaletteSnapshot, () => null);
}

// =============================================================================
// "Original" escape hatch
//
// Superhuman has no way out of its transform, which is why its Discourse thread
// about unreadable mail has no answer in it. Some senders' HTML is simply not
// survivable — and the fix should be made once, for that sender, not per
// message.
// =============================================================================

export const ORIGINAL_DOMAINS_KEY = 'kiln.originalMail';

export function readOriginalDomains(): string[] {
  try {
    const raw = localStorage.getItem(ORIGINAL_DOMAINS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').map((v) => v.toLowerCase());
  } catch {
    return [];
  }
}

export function writeOriginalDomains(domains: string[]): void {
  try {
    localStorage.setItem(ORIGINAL_DOMAINS_KEY, JSON.stringify(domains));
  } catch {
    /* a preference is not worth a thrown render */
  }
}

// =============================================================================
// Preparation
// =============================================================================

const QUOTE_SELECTOR = [
  'blockquote.gmail_quote',
  'div.gmail_quote',
  'div.gmail_extra',
  '#divRplyFwdMsg',
  'blockquote[type="cite"]',
  '.moz-cite-prefix',
  'div.yahoo_quoted',
  'div.protonmail_quote',
].join(',');

const WROTE_RE = /^on\s.{4,160}?\bwrote:\s*$/is;
const ORIGINAL_RE = /^-{2,}\s*(original message|forwarded message)\s*-{2,}$/i;
const OUTLOOK_DIVIDER_RE = /^_{10,}$/;
const HEADER_BLOCK_RE = /^from:.+\b(sent|date):/is;
const SIGDASH_RE = /^--\s?$/;

export type MessageSegmentKind = 'body' | 'signature' | 'quote';

export interface MessageSegment {
  kind: MessageSegmentKind;
  html: string;
}

export interface PreparedMessage {
  segments: MessageSegment[];
  /** Every remote URL found, whether or not it was substituted this pass. */
  blocked: string[];
  trackers: number;
}

export interface PrepareOptions {
  messageId: number;
  attachments: AttachmentView[];
  allowRemote: boolean;
  signed?: ReadonlyMap<string, string>;
  collapseQuotes?: boolean;
  /** Null leaves the sender's colours exactly as authored. */
  palette?: Palette | null;
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isRemote(url: string): boolean {
  return /^(https?:)?\/\//i.test(url.trim());
}

function looksLikeTracker(url: string, el: Element): boolean {
  const w = Number(el.getAttribute('width') ?? NaN);
  const h = Number(el.getAttribute('height') ?? NaN);
  if (Number.isFinite(w) && Number.isFinite(h) && w <= 2 && h <= 2) return true;
  const style = el.getAttribute('style') ?? '';
  if (/(^|[;\s])width:\s*[012](px)?\s*(;|$)/i.test(style)) return true;
  return /(\/|[?&])(open|track|beacon|pixel|impression|wf\/open)([./?&=]|$)/i.test(url);
}

function rewriteCssUrls(
  css: string,
  opts: PrepareOptions,
  cidMap: Map<string, string>,
  found: string[],
): string {
  return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (match, _quote: string, raw: string) => {
    const url = raw.trim();
    if (url.startsWith('data:')) return match;
    if (url.toLowerCase().startsWith('cid:')) {
      const mapped = cidMap.get(url.slice(4).replace(/^<|>$/g, '').toLowerCase());
      return mapped ? `url("${mapped}")` : `url("${TRANSPARENT_PX}")`;
    }
    if (!isRemote(url)) return `url("${TRANSPARENT_PX}")`;
    found.push(url);
    const signed = opts.allowRemote ? opts.signed?.get(url) : undefined;
    return signed ? `url("${signed}")` : `url("${TRANSPARENT_PX}")`;
  });
}

function serializeNodes(nodes: Node[]): string {
  return nodes
    .map((n) => {
      if (n.nodeType === Node.ELEMENT_NODE) return (n as Element).outerHTML;
      if (n.nodeType === Node.TEXT_NODE) return escapeHtml(n.textContent ?? '');
      return '';
    })
    .join('');
}

/** Re-wrap a slice of children in the ancestor chain it was cut out of. */
function wrapChain(nodes: Node[], container: Element, root: Element): string {
  let html = serializeNodes(nodes);
  let el: Element | null = container;
  while (el && el !== root) {
    const shell = el.cloneNode(false) as Element;
    shell.innerHTML = html;
    html = shell.outerHTML;
    el = el.parentElement;
  }
  return html;
}

function isQuoteMarker(el: Element): boolean {
  if (el.matches(QUOTE_SELECTOR)) return true;
  const text = (el.textContent ?? '').trim();
  if (!text) return false;
  if (text.length > 400) return false;
  return (
    WROTE_RE.test(text) ||
    ORIGINAL_RE.test(text) ||
    OUTLOOK_DIVIDER_RE.test(text) ||
    HEADER_BLOCK_RE.test(text)
  );
}

/**
 * Descend past wrappers that contain the whole message, otherwise a quote
 * nested three divs deep would classify the entire body as quoted history.
 */
function narrowContainer(root: Element): Element {
  let container = root;
  for (;;) {
    const children = Array.from(container.children);
    const only = children[0];
    if (children.length !== 1 || !only) return container;
    if (isQuoteMarker(only)) return container;
    if (!only.querySelector(QUOTE_SELECTOR) && !Array.from(only.children).some(isQuoteMarker)) {
      return container;
    }
    container = only;
  }
}

function splitQuoted(root: Element): { body: Node[]; signature: Node[]; quote: Node[]; container: Element } {
  const container = narrowContainer(root);
  const children = Array.from(container.childNodes);
  const elementIndex = new Map<Element, number>();
  children.forEach((n, i) => {
    if (n.nodeType === Node.ELEMENT_NODE) elementIndex.set(n as Element, i);
  });

  let cut = -1;
  for (const [el, index] of elementIndex) {
    if (!isQuoteMarker(el)) continue;
    if (cut < 0 || index < cut) cut = index;
  }
  // A quote node deeper than the top level still marks its top-level ancestor.
  for (const node of Array.from(container.querySelectorAll(QUOTE_SELECTOR))) {
    let el: Element | null = node;
    while (el && el.parentElement !== container) el = el.parentElement;
    if (!el) continue;
    const index = elementIndex.get(el);
    if (index != null && (cut < 0 || index < cut)) cut = index;
  }

  // Collapsing the entire message is never right — a pure forward has no
  // "original" part to keep visible.
  if (cut <= 0) return { body: children, signature: [], quote: [], container };

  const body = children.slice(0, cut);
  const quote = children.slice(cut);

  let sigStart = -1;
  for (let i = body.length - 1; i >= 0 && body.length - i <= 8; i -= 1) {
    const text = (body[i]?.textContent ?? '').trim();
    if (SIGDASH_RE.test(text)) {
      sigStart = i;
      break;
    }
  }
  if (sigStart > 0) {
    return { body: body.slice(0, sigStart), signature: body.slice(sigStart), quote, container };
  }
  return { body, signature: [], quote, container };
}

/**
 * Pure, and therefore testable: parse, neutralise every remote reference, apply
 * the dark transform if one was asked for, then cut the quoted history out into
 * its own segment. Parsing happens in an inert document, so nothing is fetched
 * and nothing runs — which is precisely why the transform can live here rather
 * than needing script inside the sandbox.
 */
export function prepareMessageHtml(html: string, opts: PrepareOptions): PreparedMessage {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const root = doc.body;
  const found: string[] = [];
  let trackers = 0;

  const cidMap = new Map<string, string>();
  for (const att of opts.attachments ?? []) {
    if (!att.contentId) continue;
    cidMap.set(att.contentId.replace(/^<|>$/g, '').toLowerCase(), api.attachmentUrl(opts.messageId, att.partId));
  }

  for (const img of Array.from(root.querySelectorAll('img'))) {
    const src = (img.getAttribute('src') ?? '').trim();

    const srcset = img.getAttribute('srcset');
    if (srcset) {
      const parts = srcset.split(',').map((p) => p.trim()).filter(Boolean);
      for (const part of parts) {
        const url = part.split(/\s+/)[0] ?? '';
        if (isRemote(url)) found.push(url);
      }
      const rebuilt = opts.allowRemote
        ? parts
            .map((part) => {
              const bits = part.split(/\s+/);
              const signedUrl = opts.signed?.get(bits[0] ?? '');
              return signedUrl ? [signedUrl, ...bits.slice(1)].join(' ') : null;
            })
            .filter((v): v is string => v !== null)
            .join(', ')
        : '';
      if (rebuilt) img.setAttribute('srcset', rebuilt);
      else img.removeAttribute('srcset');
    }

    if (!src || src.startsWith('data:')) continue;

    if (src.toLowerCase().startsWith('cid:')) {
      const mapped = cidMap.get(src.slice(4).replace(/^<|>$/g, '').toLowerCase());
      img.setAttribute('src', mapped ?? TRANSPARENT_PX);
      continue;
    }

    if (!isRemote(src)) {
      img.setAttribute('src', TRANSPARENT_PX);
      continue;
    }

    found.push(src);
    if (looksLikeTracker(src, img)) trackers += 1;

    const signed = opts.allowRemote ? opts.signed?.get(src) : undefined;
    if (signed) {
      img.setAttribute('src', signed);
    } else {
      // width/height attributes stay untouched so the layout keeps its shape.
      img.setAttribute('data-blocked-src', src);
      img.setAttribute('src', TRANSPARENT_PX);
    }
  }

  for (const styleEl of Array.from(root.querySelectorAll('style'))) {
    styleEl.textContent = rewriteCssUrls(styleEl.textContent ?? '', opts, cidMap, found);
  }
  for (const el of Array.from(root.querySelectorAll('[style]'))) {
    const style = el.getAttribute('style') ?? '';
    if (style.includes('url(')) el.setAttribute('style', rewriteCssUrls(style, opts, cidMap, found));
  }
  for (const el of Array.from(root.querySelectorAll('[background]'))) {
    el.removeAttribute('background');
  }

  // After the URL rewrite, so the colour pass sees the final `url()` tokens and
  // leaves them alone; before the split, so every segment is transformed.
  if (opts.palette) applyDarkTransform(root, opts.palette);

  const uniqueRemote = [...new Set(found)];

  if (opts.collapseQuotes === false) {
    return { segments: [{ kind: 'body', html: root.innerHTML }], blocked: uniqueRemote, trackers };
  }

  const { body, signature, quote, container } = splitQuoted(root);
  const segments: MessageSegment[] = [{ kind: 'body', html: wrapChain(body, container, root) }];
  if (signature.length > 0) {
    segments.push({ kind: 'signature', html: wrapChain(signature, container, root) });
  }
  if (quote.length > 0) {
    segments.push({ kind: 'quote', html: wrapChain(quote, container, root) });
  }
  return { segments, blocked: uniqueRemote, trackers };
}

export interface TextSegment {
  kind: MessageSegmentKind;
  text: string;
}

/**
 * Plain-text bodies never enter an iframe: there is nothing to sandbox, and
 * rendering them in the parent is the only way they can follow the app theme.
 * They therefore need no dark transform at all — they are already native.
 */
export function splitPlainText(text: string, collapseQuotes = true): TextSegment[] {
  const lines = text.split(/\r?\n/);
  if (!collapseQuotes) return [{ kind: 'body', text }];

  let cut = -1;
  let sig = -1;

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i] ?? '';
    const line = raw.trim();
    if (cut < 0 && (WROTE_RE.test(line) || ORIGINAL_RE.test(line) || OUTLOOK_DIVIDER_RE.test(line))) {
      cut = i;
    }
    if (cut < 0 && i > 0 && line.startsWith('>')) cut = i;
    if (SIGDASH_RE.test(raw)) sig = i;
  }

  const end = cut < 0 ? lines.length : cut;
  const sigStart = sig > 0 && sig < end ? sig : -1;
  const bodyEnd = sigStart >= 0 ? sigStart : end;

  const segments: TextSegment[] = [{ kind: 'body', text: lines.slice(0, bodyEnd).join('\n').trimEnd() }];
  if (sigStart >= 0) segments.push({ kind: 'signature', text: lines.slice(sigStart, end).join('\n') });
  if (cut >= 0) segments.push({ kind: 'quote', text: lines.slice(cut).join('\n') });
  return segments;
}

// =============================================================================
// Rendering
// =============================================================================

const MIN_SCALE = 0.6;
const UNMEASURED_HEIGHT = 240;

function HtmlFrame({
  html,
  title,
  palette,
}: {
  html: string;
  title: string;
  palette: Palette | null;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const [fullWidth, setFullWidth] = useState(false);

  const srcDoc = useMemo(() => buildSrcDoc(html, palette), [html, palette]);

  const measure = useCallback(() => {
    const frame = frameRef.current;
    const wrap = wrapRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !wrap || !doc?.documentElement) return;

    const available = wrap.clientWidth;
    if (available <= 0) return;

    const natural = Math.max(doc.documentElement.scrollWidth, doc.body?.scrollWidth ?? 0);
    const overflowing = natural > available + 1;

    setContentWidth(overflowing ? natural : null);
    setScale(overflowing && !fullWidth ? Math.max(MIN_SCALE, available / natural) : 1);

    const next = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0);
    setHeight((prev) => (prev != null && Math.abs(prev - next) < 1 ? prev : next));
  }, [fullWidth]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    let observer: ResizeObserver | null = null;
    let imgListeners: (() => void)[] = [];
    // Keyed on document identity, not a boolean: swapping srcDoc creates a new
    // document, and a flag would leave the new one unmeasured forever.
    let attachedDoc: Document | null = null;

    const detach = () => {
      observer?.disconnect();
      observer = null;
      for (const off of imgListeners) off();
      imgListeners = [];
    };

    const attach = () => {
      const doc = frame.contentDocument;
      if (!doc || doc === attachedDoc) return;
      detach();
      attachedDoc = doc;
      measure();

      observer = new ResizeObserver(() => measure());
      observer.observe(doc.documentElement);
      if (doc.body) observer.observe(doc.body);

      // Images arrive late and change the height; each one re-measures.
      for (const img of Array.from(doc.images)) {
        if (img.complete) continue;
        const onSettled = () => measure();
        img.addEventListener('load', onSettled);
        img.addEventListener('error', onSettled);
        imgListeners.push(() => {
          img.removeEventListener('load', onSettled);
          img.removeEventListener('error', onSettled);
        });
      }
    };

    frame.addEventListener('load', attach);
    if (frame.contentDocument?.readyState === 'complete') attach();

    const wrapObserver = new ResizeObserver(() => measure());
    const wrap = wrapRef.current;
    if (wrap) wrapObserver.observe(wrap);

    return () => {
      frame.removeEventListener('load', attach);
      detach();
      wrapObserver.disconnect();
    };
  }, [measure, srcDoc]);

  const scaled = scale < 1;
  const frameWidth = contentWidth != null ? contentWidth : '100%';
  const outerHeight = height != null ? Math.ceil(height * scale) : UNMEASURED_HEIGHT;

  return (
    <>
      <div
        ref={wrapRef}
        className={styles.frameWrap}
        data-overflow={fullWidth && contentWidth != null}
        style={{ height: outerHeight }}
      >
        <iframe
          ref={frameRef}
          className={styles.frame}
          title={title}
          sandbox={MESSAGE_SANDBOX}
          srcDoc={srcDoc}
          referrerPolicy="no-referrer"
          loading="lazy"
          style={{
            width: typeof frameWidth === 'number' ? `${frameWidth}px` : frameWidth,
            height: height != null ? `${height}px` : `${UNMEASURED_HEIGHT}px`,
            transform: scaled ? `scale(${scale})` : undefined,
            colorScheme: palette ? 'dark' : 'light',
          }}
        />
      </div>
      {(scaled || fullWidth) && contentWidth != null && (
        <div className={styles.scaleBar}>
          <span>{scaled ? `Scaled to fit · ${Math.round(scale * 100)}%` : 'Full width'}</span>
          <span aria-hidden="true">·</span>
          <button type="button" className={styles.scaleAction} onClick={() => setFullWidth((v) => !v)}>
            {fullWidth ? 'Scale to fit' : 'Show full width'}
          </button>
        </div>
      )}
    </>
  );
}

export interface MessageBodyProps {
  message: MessageView;
  /** Set when the sender's domain is on the always-allow list. */
  allowRemoteImages?: boolean;
  /**
   * Load remote images for every sender without asking. Still proxied, so the
   * sender sees this server rather than the reader.
   */
  autoLoadImages?: boolean;
  onAllowDomain?: (domain: string) => void;
  /** Off for previews and printing, where the quoted history should be inline. */
  collapseQuotes?: boolean;
  /**
   * Render the sender's own colours on light paper, skipping the dark
   * transform. Uncontrolled callers get the per-domain preference; the reading
   * pane passes it explicitly because it also owns the toggle.
   */
  original?: boolean;
  className?: string;
}

export function MessageBody({
  message,
  allowRemoteImages = false,
  autoLoadImages = false,
  onAllowDomain,
  collapseQuotes = true,
  original,
  className,
}: MessageBodyProps) {
  /**
   * Always starts false, even when remote images are wanted.
   *
   * `allowRemote` does not mean "the user consented" — it means "the signed
   * proxy URLs are ready". Seeding it true skipped the signing step entirely:
   * the renderer looked for a signed URL, found none, and emitted nothing, so
   * an allow-listed sender's images silently never appeared. The effect below
   * signs first and flips this afterwards.
   */
  const [allowRemote, setAllowRemote] = useState(false);
  const [signed, setSigned] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [loadingImages, setLoadingImages] = useState(false);
  const autoSignRequested = useRef(false);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const theme = useMessageTheme();

  const domain = domainOf(message.from.address);
  const [storedOriginal] = useState<string[]>(readOriginalDomains);
  const showOriginal =
    original ?? (domain !== '' && storedOriginal.includes(domain));

  // A new message is a new decision about remote content; nothing carries over.
  useEffect(() => {
    setExpanded(new Set());
    setSigned(new Map());
    setAllowRemote(false);
    autoSignRequested.current = false;
  }, [message.id, allowRemoteImages, autoLoadImages]);

  const palette = showOriginal ? null : theme;

  const prepared = useMemo<PreparedMessage | null>(() => {
    if (!message.bodyHtml) return null;
    return prepareMessageHtml(message.bodyHtml, {
      messageId: message.id,
      attachments: message.attachments,
      allowRemote,
      signed,
      collapseQuotes,
      palette,
    });
  }, [message.bodyHtml, message.id, message.attachments, allowRemote, signed, collapseQuotes, palette]);

  const textSegments = useMemo<TextSegment[] | null>(
    () => (message.bodyHtml ? null : splitPlainText(message.bodyText ?? '', collapseQuotes)),
    [message.bodyHtml, message.bodyText, collapseQuotes],
  );

  const blocked = prepared?.blocked ?? [];
  const pendingRemote = blocked.length > 0 && !allowRemote;

  const showImages = useCallback(async () => {
    setLoadingImages(true);
    const results = await Promise.allSettled(blocked.map((url) => api.imgsign(url)));
    const map = new Map<string, string>();
    results.forEach((result, i) => {
      const original_ = blocked[i];
      if (original_ && result.status === 'fulfilled') map.set(original_, result.value.src);
    });
    setSigned(map);
    setAllowRemote(true);
    setLoadingImages(false);
  }, [blocked]);

  /**
   * Load remote images without being asked, when the preference (or a
   * per-sender allow-list entry) says so.
   *
   * This still goes through the signing step and the server-side proxy — the
   * fetch is made by the server, so the sender learns nothing about the reader
   * beyond "opened", and no cookies, referrer or user agent leave this browser.
   * `autoSignRequested` guards against re-entry: `blocked` is a fresh array
   * every render, so keying the effect on it directly would loop.
   */
  useEffect(() => {
    if (!(autoLoadImages || allowRemoteImages)) return;
    if (allowRemote || loadingImages || autoSignRequested.current) return;
    if (blocked.length === 0) return;
    autoSignRequested.current = true;
    void showImages();
  }, [autoLoadImages, allowRemoteImages, allowRemote, loadingImages, blocked.length, showImages]);

  const toggleSegment = useCallback((index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  if (!message.bodyHtml && (!message.bodyText || message.bodyText.trim() === '')) {
    return (
      <div className={`${styles.root} ${className ?? ''}`}>
        <p className={styles.plain}>This message has no text content.</p>
      </div>
    );
  }

  return (
    <div
      className={`${styles.root} ${className ?? ''}`}
      // Drives the paper treatment: transparent on the app surface once the
      // body has been transformed, white paper whenever it has not.
      data-paper={palette ? 'none' : 'light'}
    >
      {prepared && pendingRemote && (
        <div className={styles.banner}>
          <span className={styles.bannerText}>
            Images blocked
            {prepared.trackers > 0 &&
              ` · ${prepared.trackers} tracking ${prepared.trackers === 1 ? 'pixel' : 'pixels'}`}
          </span>
          <button
            type="button"
            className={styles.bannerAction}
            onClick={() => void showImages()}
            disabled={loadingImages}
          >
            {loadingImages ? 'Loading…' : 'Show images'}
          </button>
          {domain && onAllowDomain && (
            <button
              type="button"
              className={styles.bannerAction}
              onClick={() => {
                onAllowDomain(domain);
                void showImages();
              }}
              disabled={loadingImages}
            >
              Always for {domain}
            </button>
          )}
        </div>
      )}

      {prepared?.segments.map((segment, index) => {
        if (segment.kind === 'body') {
          return (
            <HtmlFrame
              key={`body-${index}`}
              html={segment.html}
              title={message.subject || 'Message'}
              palette={palette}
            />
          );
        }
        const open = expanded.has(index);
        return (
          <div key={`${segment.kind}-${index}`} className={styles.quoteBlock}>
            <button
              type="button"
              className={styles.ellipsis}
              aria-expanded={open}
              title={segment.kind === 'signature' ? 'Show signature' : 'Show quoted text'}
              aria-label={segment.kind === 'signature' ? 'Show signature' : 'Show quoted text'}
              onClick={() => toggleSegment(index)}
            >
              •••
            </button>
            {open && (
              <HtmlFrame
                html={segment.html}
                title={segment.kind === 'signature' ? 'Signature' : 'Quoted text'}
                palette={palette}
              />
            )}
          </div>
        );
      })}

      {textSegments?.map((segment, index) => {
        if (segment.kind === 'body') {
          return (
            <pre key={`text-${index}`} className={styles.plain}>
              {segment.text}
            </pre>
          );
        }
        const open = expanded.has(index);
        return (
          <div key={`${segment.kind}-${index}`} className={styles.quoteBlock}>
            <button
              type="button"
              className={styles.ellipsis}
              aria-expanded={open}
              title={segment.kind === 'signature' ? 'Show signature' : 'Show quoted text'}
              aria-label={segment.kind === 'signature' ? 'Show signature' : 'Show quoted text'}
              onClick={() => toggleSegment(index)}
            >
              •••
            </button>
            {open && <pre className={styles.plain}>{segment.text}</pre>}
          </div>
        );
      })}
    </div>
  );
}

export default MessageBody;
